import React, { useMemo } from "react";
import { motion } from "framer-motion";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { format, parseISO } from "date-fns";

type TokenType = "" | "NAU" | "NAUY" | "NAUN";

interface ChartSectionProps {
  data: {
    date: string;
    NAUBurn: number;
    NAUYMint: number;
    NAUNMint: number;
  }[];
  token?: TokenType;
}

// IMPORTANT: match backend keys exactly
const linesConfig = [
  { key: "NAUBurn", stroke: "#000000", name: "🔥 NAU Burn" },
  { key: "NAUYMint", stroke: "#1e40af", name: "🔵 NAUY Mint" },
  { key: "NAUNMint", stroke: "#b91c1c", name: "🔴 NAUN Mint" },
] as const;

const ChartSection: React.FC<ChartSectionProps> = ({ data, token = "" }) => {
  const filteredLines = useMemo(
    () =>
      linesConfig
        .filter((line) => token === "" || line.key.includes(token))
        .map((line) => (
          <Line
            key={line.key}
            type="monotone"
            dataKey={line.key}
            stroke={line.stroke}
            dot={{ r: 4 }}
            name={line.name}
          />
        )),
    [token]
  );

  return (
    <motion.div
      role="region"
      aria-labelledby="chart-title"
      className="md:mt-10"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <h2
        id="chart-title"
        className="text-4xl font-bold mb-6 text-center text-red-900"
      >
        Daily Burn & Mint Activity
      </h2>

      <div className="flex flex-col gap-6">
        {/* Description */}
        <div className="flex flex-col lg:flex-row items-start w-full max-w-7xl mx-auto bg-white/90 p-8 rounded-2xl shadow-xl mt-8 gap-8">
          <p className="text-gray-700 text-sm leading-relaxed">
            Observe real-time user sentiment in action. <strong>NAU</strong>{" "}
            burns reflect commitment, while <strong>NAUY</strong> and{" "}
            <strong>NAUN</strong> minting reveal directional opinion expression.
            Spikes often correspond to major debates, events, or trending
            topics. This time-series view helps you identify surges in
            participation and polarization across the network.
          </p>
        </div>

        {/* Chart */}
        <div className="flex flex-col items-center justify-center lg:flex-row w-full max-w-6xl mx-auto bg-white/90 p-8 rounded-2xl shadow-xl mt-8 gap-8">
          <div className="flex-1 flex flex-col lg:flex-row items-start w-full max-w-5xl mx-auto mt-8 gap-8">
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={data}>
                <XAxis
                  dataKey="date"
                  tickFormatter={(iso) => format(parseISO(iso), "dd/MM")}
                  stroke="#444"
                  tick={{ fontSize: 12 }}
                />
                <YAxis stroke="#444" tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value: number) => value.toLocaleString()}
                  labelFormatter={(label) => format(parseISO(label), "PP")}
                />
                <Legend
                  verticalAlign="top"
                  height={30}
                  wrapperStyle={{ paddingBottom: 10 }}
                />
                {filteredLines}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ChartSection;
